import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  HostBinding,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { ChatApiService } from '../../shared-services/chat-api.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DynamicEditTableComponent } from '../../chart-components/dynamic-edit-table/dynamic-edit-table.component';
import { StreamingTextComponent } from '../streaming-text/streaming-text.component';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { CustomChatApiService } from '../../../../../../../../custom/shared-services/custom-chat-api.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
interface Message {
  text: string;
  sender: 'user' | 'agent';
  files?: File[];
  type?: 'text' | 'table' | 'option' | 'json';
  data?: any;
  formattedText?: string;
  rawJson?: string;
  copied?: boolean;
  loading?: boolean;
  completed?: boolean;
}

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    DynamicEditTableComponent,
    StreamingTextComponent,
  ],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild(DynamicEditTableComponent) private dynamicEditTable?: DynamicEditTableComponent;
  @ViewChild('chatBody') private chatBody!: ElementRef;

  messages: Message[] = [];
  currentMessage = '';
  isLoading = false;
  selectedFiles: File[] = [];
  isDarkTheme = false;

  title = 'Chat'; // Default title
  welcomeMessage = 'Hello! How can I help you today?';
  apiUrl = '';

  private subscriptions = new Subscription();
  private previousMessageCount = 0;

  constructor(
    private chatApiService:ChatApiService,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private responsiveLayoutService: ResponsiveLayoutService,
  ) {}

  ngOnInit(): void {
    // Ensure DPR zoom compensation and CSS variables are set
    // (needed when chat is loaded directly via reload, bypassing DashboardComponent)
    this.responsiveLayoutService.init();

    // 1. Load Config if available
    const configFile = this.route.snapshot.data['configFile'];
    if (configFile) {
      this.configService.getApi(`assets/${configFile}`).subscribe({
        next: (config: any) => {
          if (config) {
            this.title = config.title || this.title;
            this.welcomeMessage = config.welcomeMessage || this.welcomeMessage;
            this.apiUrl = config.apiUrl || '';

            // Initialize messages AFTER config is loaded to show correct welcome message
            if (this.messages.length === 0) {
              this.messages.push({ text: this.welcomeMessage, sender: 'agent' });
            }
          }
          this.initSession();
        },
        error: (err) => {
          console.error('Failed to load chat config:', err);
          // Fallback initialization
          if (this.messages.length === 0) {
            this.messages.push({ text: this.welcomeMessage, sender: 'agent' });
          }
          this.initSession();
        },
      });
    } else {
      // Fallback if no config file specified
      if (this.messages.length === 0) {
        this.messages.push({ text: this.welcomeMessage, sender: 'agent' });
      }
      this.initSession();
    }
  }

  private initSession(): void {
    this.chatApiService.createSession(this.apiUrl).then((observable) => {
      observable.subscribe({
        next: (response) => console.log('Session created:', response),
        error: (error) => console.error('Error creating session:', error),
      });
    });
  }

  ngAfterViewChecked() {
    if (this.messages.length !== this.previousMessageCount) {
      this.scrollToBottom();
      this.previousMessageCount = this.messages.length;
    }
  }

  toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;

    if (this.isDarkTheme) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }

  scrollToBottom(): void {
    try {
      setTimeout(
        () => (this.chatBody.nativeElement.scrollTop = this.chatBody.nativeElement.scrollHeight),
        0,
      );
    } catch (err) {}
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  startNewSession(): void {
    this.messages = [];
    // this.messages.push({ text: 'Hello! How can I help you today?', sender: 'agent' });
    this.chatApiService.createSession().then((observable) => {
      observable.subscribe({
        next: (response) => console.log('New session created:', response),
        error: (error) => console.error('Error creating new session:', error),
      });
    });
    this.scrollToBottom();
  }

  sendMessageOnEnter(event: Event | KeyboardEvent): void {
    const keyboardEvent = event as KeyboardEvent; // cast to KeyboardEvent
    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  formatText(text: string): string {
    let formatted = text;
    // bold
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong></strong>');
    // italic
    formatted = formatted.replace(/\*(.*?)\*/g, '<em></em>');

    // Convert hyphenated lists to HTML unordered lists
    // First, convert each hyphenated line to an <li> tag
    // This handles cases where a list starts on a new line or at the beginning of the text
    formatted = formatted.replace(/^-\s(.+)$/gm, '<li></li>');

    // Then, wrap consecutive <li> tags in a <ul> tag
    // The regex ensures that <ul></ul> wraps only blocks of <li> elements
    formatted = formatted.replace(/((?:<li>.*?<\/li>\s*)+)/g, '<ul></ul>');

    return formatted;
  }

  async sendMessage(): Promise<void> {
    if ((!this.currentMessage.trim() && this.selectedFiles.length === 0) || this.isLoading) {
      return;
    }

    let tableData: any[] | undefined;
    if (this.dynamicEditTable && this.dynamicEditTable.isTableEditable()) {
      tableData = this.dynamicEditTable.getTableData();

      // Find and remove the last message containing the table
      const tableMessageIndex = this.messages.map((m) => m.type).lastIndexOf('table');
      if (tableMessageIndex > -1) {
        this.messages.splice(tableMessageIndex, 1);
      }
    }

    let jsonData: any | undefined;
    const jsonMessageIndex = this.messages.map((m) => m.type).lastIndexOf('json');
    if (jsonMessageIndex > -1) {
      const jsonMessage = this.messages[jsonMessageIndex];
      try {
        if (jsonMessage.rawJson) {
          jsonData = JSON.parse(jsonMessage.rawJson);
        }
        this.messages.splice(jsonMessageIndex, 1);
      } catch (e) {
        console.error('Invalid JSON format:', e);
        // Optionally, show an error message to the user in the chat
        this.messages.push({
          text: 'Error: Invalid JSON format. Please correct it before sending.',
          sender: 'agent',
        });
        return;
      }
    }

    const userMessage: Message = {
      text: this.currentMessage,
      sender: 'user',
      files: [...this.selectedFiles],
    };

    this.messages.push(userMessage);
    const messageToApi = this.currentMessage.trim();
    this.currentMessage = '';
    this.selectedFiles = [];
    this.isLoading = true;

    const turnStartIndex = this.messages.length;
    let accumulatedResponse = '';

    (await this.chatApiService.sendMessage(messageToApi, tableData, jsonData, this.apiUrl)).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        // Expecting response format: { message: "..." }
        const agentText = response?.message || 'No response from agent.';

        const agentMessage: Message = {
          text: agentText,
          sender: 'agent',
          formattedText: this.formatText(agentText),
        };
        this.messages.push(agentMessage);
        this.scrollToBottom();
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error('API Error:', error);
        const errorMessage = error.message || 'Sorry, something went wrong.';
        this.messages.push({
          text: errorMessage,
          sender: 'agent',
          formattedText: errorMessage,
        });
        this.scrollToBottom();
      },
    });
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList) {
      for (let i = 0; i < fileList.length; i++) {
        this.selectedFiles.push(fileList[i]);
      }
    }
  }

  removeFile(fileToRemove: File): void {
    this.selectedFiles = this.selectedFiles.filter((file) => file !== fileToRemove);
  }

  onOptionSelected(option: string): void {
    this.currentMessage = option;
    this.sendMessage();

    // Find and remove the last message containing options
    const optionMessageIndex = this.messages.map((m) => m.type).lastIndexOf('option');
    if (optionMessageIndex > -1) {
      this.messages.splice(optionMessageIndex, 1);
    }
  }

  copyJson(msg: Message): void {
    if (msg.rawJson) {
      navigator.clipboard
        .writeText(msg.rawJson)
        .then(() => {
          msg.copied = true;
          setTimeout(() => {
            msg.copied = false;
          }, 2000); // Hide message after 2 seconds
        })
        .catch((err) => {
          console.error('Failed to copy JSON: ', err);
        });
    }
  }
}
